import { useMemo, useState } from "react";
import { Chip, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GovItemDetail, GovSettingsOn } from "../../../../utils/icGovernance";
import { useViewport } from "../../../../utils/useViewport";
import { useGovernance } from "../GovernanceLayout";
import NewIssueDialog from "../dialogs/NewIssueDialog";
import { NewButton } from "../dialogs/dialogParts";
import {
  ENVIRONMENT_LABEL,
  PriorityChip,
  SignOffChip,
  StatusChip,
  clientLabel,
  dateOnly,
  hours,
  hoursVariance,
  itemUrl,
  linkedIds,
  vendorLabel,
} from "../governanceStatus";
import {
  EvidenceLinks,
  IdLink,
  LinkedIdList,
  LongText,
  MUTED,
  Muted,
  PartyChip,
  PercentBar,
  PlainText,
  TitleLink,
  VarianceText,
  localDay,
} from "./cells";
import RegisterTable, { RegisterColumn, WaitingNotice } from "./RegisterTable";
import { TRACKER_STATUSES, TrackerRow, TrackerType, useTrackerItems } from "./useTrackerItems";

/**
 * Master Project Tracker — tasks AND issues in one list (user decision
 * 2026-10-12; IC_Ticket keeps them as two registers, so `useTrackerItems`
 * merges them). Columns follow the client's workbook; an issue row fills the
 * task columns with its nearest field and leaves the task-only ones (hours
 * estimate, % complete, sign-off) blank.
 */

const TYPE_OPTIONS: { value: TrackerType; label: string }[] = [
  { value: "all", label: "Tasks and issues" },
  { value: "tasks", label: "Tasks" },
  { value: "issues", label: "Issues" },
];

const TypeChip = ({ row }: { row: TrackerRow }) => (
  <Chip
    size="small"
    variant="outlined"
    label={row.kind === "TASK" ? "Task" : "Issue"}
    sx={{
      fontWeight: 600,
      color: row.kind === "TASK" ? "#1a56c4" : "#b45309",
      borderColor: row.kind === "TASK" ? "#1a56c4" : "#b45309",
      backgroundColor: row.kind === "TASK" ? "#e8f0fe" : "#fff1e0",
    }}
  />
);

const trackerColumns = (s: GovSettingsOn): RegisterColumn<TrackerRow>[] => [
  { key: "id", header: "ID", render: (r) => <IdLink item={r} /> },
  { key: "type", header: "Type", render: (r) => <TypeChip row={r} /> },
  {
    key: "workstream",
    header: "Workstream",
    render: (r) =>
      r.kind === "TASK" ? (
        <PlainText value={r.workstream} />
      ) : (
        <PlainText value={r.environment ? ENVIRONMENT_LABEL[r.environment] ?? r.environment : null} />
      ),
  },
  { key: "title", header: "Task / Issue", wrap: true, minWidth: 220, render: (r) => <TitleLink item={r} /> },
  {
    key: "acceptance",
    header: "Description / Acceptance Criteria",
    wrap: true,
    minWidth: 280,
    render: (r) => <LongText value={r.kind === "TASK" ? r.acceptanceCriteria || r.description : r.description} />,
  },
  { key: "leadParty", header: "Lead Party", render: (r) => <PartyChip party={r.leadParty} settings={s} /> },
  { key: "clientOwner", header: `${clientLabel(s)} Lead / Role`, render: (r) => <PlainText value={r.clientOwner} /> },
  {
    key: "vendorOwner",
    header: `${vendorLabel(s)} Lead / Role`,
    render: (r) => <PlainText value={r.vendorOwner || (r.kind === "ISSUE" ? r.assignedTo : null)} />,
  },
  { key: "priority", header: "Priority", render: (r) => <PriorityChip priority={r.priority} /> },
  {
    key: "startDate",
    header: "Start Date",
    render: (r) => (r.kind === "TASK" ? dateOnly(r.startDate) : localDay(r.reportedAt || r.createdAt)),
  },
  { key: "status", header: "Status", render: (r) => <StatusChip kind={r.kind} status={r.status} /> },
  {
    key: "percent",
    header: "% Complete",
    render: (r) => (r.kind === "TASK" ? <PercentBar value={r.percentComplete} /> : <Muted />),
  },
  { key: "dueDate", header: "Date Due", render: (r) => dateOnly(r.dueDate) },
  {
    key: "estHours",
    header: "Est. Hours",
    align: "right",
    render: (r) => (r.kind === "TASK" ? hours(r.estimatedMinutes) : "—"),
  },
  { key: "actualHours", header: "Actual Hours", align: "right", render: (r) => hours(r.actualMinutes) },
  {
    key: "variance",
    header: "Hours Variance",
    align: "right",
    render: (r) =>
      r.kind === "TASK" ? <VarianceText text={hoursVariance(r.actualMinutes, r.estimatedMinutes)} /> : "—",
  },
  { key: "related", header: "Dependency / Related ID", render: (r) => <LinkedIdList ids={linkedIds(r)} /> },
  {
    key: "evidence",
    header: "Deliverable / Evidence Link",
    minWidth: 160,
    render: (r) => <EvidenceLinks evidence={r.evidence} />,
  },
  {
    key: "signOffBy",
    header: `${clientLabel(s)} Sign-Off By`,
    render: (r) => (r.kind === "TASK" ? <PlainText value={r.signOff?.decidedBy} /> : <Muted />),
  },
  {
    key: "signOffStatus",
    header: "Sign-Off Status",
    render: (r) => (r.kind === "TASK" ? <SignOffChip status={r.signOff?.status} /> : <Muted />),
  },
  {
    key: "signOffDate",
    header: "Sign-Off Date",
    render: (r) => (r.kind === "TASK" ? localDay(r.signOff?.decidedAt) : "—"),
  },
];

const WAITING: WaitingNotice<TrackerRow> = {
  reason: (r) =>
    r.kind === "TASK"
      ? r.signOff?.status === "PENDING"
        ? "sign-off"
        : null
      : r.status === "RETEST"
        ? "retest"
        : null,
  describe: (n, reason) =>
    reason === "sign-off"
      ? `${n} ${n === 1 ? "task waits" : "tasks wait"} for your sign-off`
      : `${n} ${n === 1 ? "issue is" : "issues are"} ready for you to retest and close`,
};

type TypeFilterProps = { value: TrackerType; onChange: (next: TrackerType) => void; compact: boolean };

const TypeFilter = ({ value, onChange, compact }: TypeFilterProps) => (
  <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: "wrap", mb: 1.5 }}>
    {compact ? (
      <TextField
        select
        size="small"
        label="Show"
        value={value}
        onChange={(e) => onChange(e.target.value as TrackerType)}
        sx={{ minWidth: 200, backgroundColor: "white" }}
      >
        {TYPE_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    ) : (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={value}
        onChange={(_, next) => next && onChange(next as TrackerType)}
        aria-label="Show tasks, issues or both"
      >
        {TYPE_OPTIONS.map((o) => (
          <ToggleButton key={o.value} value={o.value} sx={{ textTransform: "none", whiteSpace: "nowrap", px: 1.5 }}>
            {o.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    )}
    <span style={{ color: MUTED, fontSize: 13 }}>Issues also appear on the Issue and Bug Log tab.</span>
  </Stack>
);

const NOUNS: Record<TrackerType, { one: string; many: string }> = {
  all: { one: "item", many: "tasks and issues" },
  tasks: { one: "task", many: "tasks" },
  issues: { one: "issue", many: "issues" },
};

const TrackerRegister = () => {
  const { settings } = useGovernance();
  const navigate = useNavigate();
  const { isPhone } = useViewport();
  const register = useTrackerItems();
  const columns = useMemo(() => trackerColumns(settings), [settings]);
  const [newOpen, setNewOpen] = useState(false);

  const onCreated = (item: GovItemDetail) => {
    setNewOpen(false);
    navigate(itemUrl(item));
  };

  const exports =
    register.type === "tasks"
      ? [{ kind: "TASK" as const, label: "Export tasks .xlsx" }]
      : register.type === "issues"
        ? [{ kind: "ISSUE" as const, label: "Export issues .xlsx" }]
        : [
            { kind: "TASK" as const, label: "Export tasks .xlsx" },
            { kind: "ISSUE" as const, label: "Export issues .xlsx" },
          ];

  return (
    <>
      <RegisterTable
        {...register}
        kind="TASK"
        statuses={TRACKER_STATUSES}
        exports={exports}
        columns={columns}
        noun={NOUNS[register.type]}
        caption={`${vendorLabel(settings)} maintains; ${clientLabel(settings)} signs off tasks and retests issues`}
        quickFilters={<TypeFilter value={register.type} onChange={register.onTypeChange} compact={isPhone} />}
        actions={<NewButton label="New issue" onClick={() => setNewOpen(true)} />}
        waiting={WAITING}
      />
      <NewIssueDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={onCreated} />
    </>
  );
};

export default TrackerRegister;
